import {
  EmailAuthProvider,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  User,
  getMultiFactorResolver,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { AddButton, Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { getErrorMessage } from '../../../constants/firebase-auth-errors';
import { userAsPromise } from '../../../data/providers/userProvider';
import { Column } from '../../layout/Column';
import { LoginState } from '../../pages/login/Login';
import { SecondFactor } from '../../pages/login/components/SecondFactor/SecondFactor';
import { SecondFactorVerification } from '../../pages/login/components/SecondFactorVerification';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { ErrorMessage } from '../../shared/form/ErrorMessage';
import { TextField } from '../../shared/form/TextField';
import { LinearLoading } from '../../shared/loading/LinearLoading';

interface IReautheticateProps {
  onCancel: () => void;
  user: User;
}

export const ReauthenticatePopup = withRouter(
  (props: IReautheticateProps & RouteComponentProps): React.ReactElement => {
    const [password, setPassword] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [verificationCode, setVerificationCode] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const resolver = React.useRef<MultiFactorResolver | null>(null);
    const verificationId = React.useRef<string | null>(null);
    const userInfo = React.useRef<{
      projectId: string;
      providers: string[];
    } | null>(null);

    const recaptchaVerifier = React.useRef<RecaptchaVerifier | null>(null);

    const [multiFactorHints, setMultiFactorHints] = React.useState<
      MultiFactorInfo[]
    >([]);
    const [selectedFactor, setSelectedFactor] =
      React.useState<MultiFactorInfo | null>(null);

    const [loginState, setLoginState] = React.useState<LoginState>(
      LoginState.AUTHENTICATE
    );

    const nextState = (state: LoginState): void => {
      setError('');
      setLoginState(state);
    };

    const sendMultiFactorCode = async (): Promise<void> => {
      if (!selectedFactor) {
        setError('Please select a verification method.');
        return;
      }
      const user = await userAsPromise();
      const phoneInfoOptions = {
        multiFactorHint: selectedFactor as PhoneMultiFactorInfo,
        session: resolver.current.session,
      };
      const phoneAuthProvider = new PhoneAuthProvider(user.services.auth);
      setLoading(true);

      try {
        verificationId.current = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier.current
        );
        setLoading(false);
      } catch (err) {
        setError(getErrorMessage(err.code));
        setLoading(false);
        return;
      }
      nextState(LoginState.SECOND_FACTOR_VERIFICATION);
    };

    /**
     * @description handles the user submitting form
     */
    const onSubmit = async (
      e: React.MouseEvent | React.FormEvent
    ): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();
      switch (loginState) {
        case LoginState.AUTHENTICATE: {
          setLoading(true);
          try {
            const cred = EmailAuthProvider.credential(
              props.user.email,
              password
            );

            await reauthenticateWithCredential(props.user, cred);
            props.user.reload();
            props.onCancel();
          } catch (err) {
            if (err.code === 'auth/multi-factor-auth-required') {
              const user = await userAsPromise();
              setLoading(false);

              resolver.current = getMultiFactorResolver(
                user.services.auth,
                err
              );
              setMultiFactorHints(resolver.current.hints);
              nextState(LoginState.SECOND_FACTOR_REQUIRED);
              recaptchaVerifier.current = new RecaptchaVerifier(
                user.services.auth,
                'recaptcha-container',
                {
                  size: 'invisible',
                  callback: (): void => null,
                  'expired-callback': (): void => null,
                }
              );
            } else {
              setError(getErrorMessage(err.code));
              setLoading(false);
            }
          }
          break;
        }

        case LoginState.SECOND_FACTOR_REQUIRED: {
          await sendMultiFactorCode();
          break;
        }
        case LoginState.SECOND_FACTOR_VERIFICATION: {
          setLoading(true);
          const cred = PhoneAuthProvider.credential(
            verificationId.current,
            verificationCode
          );
          const multiFactorAssertion =
            PhoneMultiFactorGenerator.assertion(cred);
          try {
            await resolver.current.resolveSignIn(multiFactorAssertion);
            props.user.reload();
            props.onCancel();
          } catch (err) {
            setError(getErrorMessage(err.code));
          }
          setLoading(false);
          break;
        }
      }
    };
    return (
      <Card size='small'>
        <Column>
          <CardHeader surface={8}>
            <CardHeaderLeft>
              <h5>Re-authenticate</h5>
            </CardHeaderLeft>
            <CardHeaderRight>
              <CloseButton onClick={props.onCancel} />
            </CardHeaderRight>
          </CardHeader>
          <CardBody>
            {loading && <LinearLoading />}
            <div style={{ padding: '20px' }}>
              {loginState === LoginState.AUTHENTICATE && (
                <React.Fragment>
                  <ControlSpacer>
                    <TextField
                      name='password'
                      label='Password'
                      type='password'
                      value={password}
                      placeholder='Enter your password...'
                      onChange={setPassword}
                    />
                  </ControlSpacer>
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                </React.Fragment>
              )}
              {loginState === LoginState.SECOND_FACTOR_REQUIRED && (
                <SecondFactor
                  hints={multiFactorHints}
                  onSelectFactor={setSelectedFactor}
                  selectedFactor={selectedFactor}
                  error={error}
                />
              )}
              {loginState === LoginState.SECOND_FACTOR_VERIFICATION && (
                <SecondFactorVerification
                  verificationCode={verificationCode}
                  onVerificationCodeChange={setVerificationCode}
                  selectedFactor={selectedFactor}
                  error={error}
                />
              )}
            </div>
          </CardBody>
          <CardFooter surface={8}>
            <CardFooterLeft></CardFooterLeft>
            <CardFooterRight>
              {loginState === LoginState.AUTHENTICATE && (
                <Button
                  title='Submit Password'
                  id='login-button'
                  aria-disabled={password.length === 0}
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={onSubmit}
                  disabled={password.length === 0}
                  type='submit'
                 />
              )}

              {loginState === LoginState.SECOND_FACTOR_REQUIRED && (
                <Button
                  title='Send Code'
                  id='login-button'
                  aria-disabled={Boolean(!selectedFactor)}
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={onSubmit}
                  disabled={Boolean(!selectedFactor)}
                  type='submit'
                 />
              )}

              {loginState === LoginState.SECOND_FACTOR_VERIFICATION && (
                <Button
                  title='Verify Code'
                  id='login-button'
                  aria-disabled={verificationCode.length < 6}
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={onSubmit}
                  disabled={verificationCode.length < 6}
                  type='submit'
                 />
              )}
            </CardFooterRight>
          </CardFooter>
        </Column>
      </Card>
    );
  }
);
